/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { Button } from 'reactstrap';
import { UniversalTrackerMin, UniversalTrackerPlain } from '@g17eco/types/universalTracker';
import { getGroup, standards } from '@g17eco/core';
import { NameCell } from '@features/custom-metrics/partials/NameCell';
import { UtrTableSearch } from './UtrTableSearch';
import './UtrTableEdit.scss';
import IconButton from '@g17eco/molecules/button/IconButton';
import { MetricDetail } from '@features/custom-metrics/partials/MetricDetail';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { Table, ColumnDef } from '@g17eco/molecules/table';

interface UtrTableEditProps {
  questions: UniversalTrackerMin[];
  currentQuestions: UniversalTrackerPlain[];
  itemName?: string;
  showHeader?: boolean;
  searchText: string;
  handleSearch: (search: string) => void;
  addToGroup: (utrId: string) => Promise<void>;
  removeFromGroup: (utrId: string) => Promise<void>;
  isUTRInGroup: (utrId: string) => boolean;
  errorMessage: string;
  isSaving: boolean;
}

export const UtrTableEdit = (props: UtrTableEditProps) => {
  const {
    questions,
    currentQuestions,
    searchText,
    handleSearch,
    addToGroup,
    removeFromGroup,
    isUTRInGroup,
    errorMessage,
    isSaving,
    itemName = 'Metric',
    showHeader = true,
  } = props;

  const [showSearchForm, setShowSearchForm] = React.useState(false);

  const getBadge = (text: string, tooltip: string, badgeColour: string, key: string) => {
    return (
      <SimpleTooltip text={tooltip} key={key}>
        <div className={`badge ${badgeColour}`}>{text}</div>
      </SimpleTooltip>
    );
  };

  const tableColumns: ColumnDef<UniversalTrackerMin>[] = [
    {
      accessorKey: '_id',
      header: '',
      meta: {
        cellProps: {
          className: 'idCol',
        },
      },
      cell: ({ row }) => {
        const id = row.original._id;
        if (isUTRInGroup(id)) {
          return (
            <IconButton
              tooltip='Remove'
              disabled={isSaving}
              color='danger'
              onClick={() => removeFromGroup(id)}
              icon='fa-times'
            />
          );
        }
        return <IconButton tooltip='Add' disabled={isSaving} onClick={() => addToGroup(id)} icon='fa-plus' />;
      },
    },
    {
      accessorKey: 'name',
      header: 'Name',
      meta: {
        cellProps: {
          className: 'nameCol',
        },
      },
      cell: ({ row }) => {
        const utr = row.original as UniversalTrackerPlain;
        return <NameCell isInGroup={isUTRInGroup(utr._id)} row={row} rowDetail={<MetricDetail utr={utr} />} />;
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      meta: {
        cellProps: {
          className: 'typeCol',
        },
        headerProps: {
          style: { width: '100px' },
        },
      },
      cell: ({ row }) => {
        const utr = row.original as UniversalTrackerPlain;
        const utrType = utr.type;
        let valueStr = utrType;
        if (utrType === 'custom_kpi') {
          valueStr = 'Custom Metric';
        }
        if (utrType === 'metric') {
          valueStr = 'Standardized Metric';
        }

        // @todo move badges to be shared with MetricTable
        const selectedBadgeColour =
          utrType === 'custom_kpi'
            ? 'background-ThemeAccentMedium text-ThemeTextWhite'
            : 'background-ThemeAccentExtralight';
        const unselectedBadgeColour =
          utrType === 'custom_kpi'
            ? 'background-ThemeTextPlaceholder text-ThemeTextWhite'
            : 'background-ThemeNeutralsLight';
        const badgeColour = isUTRInGroup(utr._id) ? selectedBadgeColour : unselectedBadgeColour;

        const text = standards[valueStr]?.shortName ?? valueStr;
        const tooltip = standards[valueStr]?.name ?? valueStr;
        const badges = [getBadge(text, tooltip, badgeColour, `${row.original._id}-${utrType}`)];

        if (utr.alternatives) {
          for (const [key] of Object.entries(utr.alternatives)) {
            const group = getGroup('standards', key);
            if (group && key !== utrType) {
              badges.push(
                getBadge(group.shortName ?? group.name, group.name, badgeColour, `${row.original._id}-${key}`)
              );
            }
          }
        }

        return <div className='text-nowrap'>{badges}</div>;
      },
    },
  ];

  const setSearchText = (e: React.ChangeEvent<any>) => {
    handleSearch(e.target.value);
  };

  const resetForms = () => {
    setShowSearchForm(false);
    handleSearch('');
  };

  const tableQuestions = showSearchForm ? questions : currentQuestions;
  return (
    <div className={'utr-table-edit-container pt-3 border-top'}>
      <Button color='primary' outline={showSearchForm} onClick={resetForms} className='p-2 mr-3'>
        Manage Metrics
      </Button>
      <Button color='primary' outline={!showSearchForm} onClick={() => setShowSearchForm(true)} className='p-2'>
        Add a new Metric
      </Button>
      {showSearchForm && (
        <>
          <h5 className='mt-3 title-container'>
            <span>
              <i className='fa fa-search mr-2' />
              Search for a {itemName}
            </span>
          </h5>
          <div className='mt-3'>
            <UtrTableSearch
              label={`Search for an existing ${itemName}`}
              filterMessage={`Add the ${itemName}`}
              acceptMessage={`Accept changes and go back to the ${itemName} list`}
              handleCancel={resetForms}
              onChange={setSearchText}
              value={searchText}
              showConfirmationButton={showHeader}
            />
          </div>
        </>
      )}
      {errorMessage && <div className='alert alert-danger'>{errorMessage}</div>}
      <div className='mt-3 metric-table-container'>
        {tableQuestions.length === 0 ? (
          <div className='alert alert-primary'>You have no metrics currently selected</div>
        ) : (
          <Table className={isSaving ? 'isSaving' : ''} columns={tableColumns} data={tableQuestions} pageSize={10} />
        )}
      </div>
    </div>
  );
};
